/**
 * Phase 1 gameStore — core gameplay state (Zustand).
 * UI-only state stays in uiStore; narrative/policy state remains in App until Wave 6.
 */
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import type {
  CityStats,
  GameStatus,
  Grid,
  ResearchState,
  SimulationBreakdown,
  StatDeltas,
} from '../types';
import { DEFAULT_UNLOCKED, INITIAL_MONEY, MILESTONES } from '../constants';
import type { Milestone } from '../types';
import { simulationEngine } from '../src/domain/services/SimulationEngine';
import { uiStore } from './uiStore';

const EMPTY_BREAKDOWN: SimulationBreakdown = {
  income: { buildings: 0, products: 0, total: 0 },
  expenses: { maintenance: 0, interest: 0, total: 0 },
  compliance: { generated: 0, decay: 0, productRisk: 0, net: 0 },
  threat: { generated: 0, mitigated: 0, net: 0 },
};

const initialStats = (): CityStats => ({
  budget: INITIAL_MONEY,
  compliance: 65,
  threat: 0,
  threatHistory: Array(20).fill(0),
  day: 1,
  loanCount: 0,
  lastPitchDay: -10,
  activeProducts: [],
});

const initialResearch = (): ResearchState => ({
  unlockedBuildings: DEFAULT_UNLOCKED,
  completedTechs: [],
  activeResearchId: null,
  researchProgress: 0,
  currentMaxLevel: 1,
});

export type GameStoreState = {
  showLanding: boolean;
  currentPage: 'landing' | 'mission' | 'howItWorks';
  gameStarted: boolean;
  gameStatus: GameStatus;
  aiEnabled: boolean;
  grid: Grid;
  stats: CityStats;
  research: ResearchState;
  tickNumber: number;
  gameSpeed: number;
  deltas: StatDeltas;
  simBreakdown: SimulationBreakdown;
  violationTiles: { x: number; y: number }[];
  milestones: Milestone[];
  /** Policy IDs that already received first-tick effect news */
  policyEffectNewsIds: string[];
};

type GameStoreActions = {
  setShowLanding: (v: boolean) => void;
  setCurrentPage: (v: GameStoreState['currentPage']) => void;
  setGameStarted: (v: boolean) => void;
  setGameStatus: (v: GameStatus) => void;
  setAiEnabled: (v: boolean) => void;
  setGrid: (grid: Grid | ((prev: Grid) => Grid)) => void;
  setStats: (stats: CityStats | ((prev: CityStats) => CityStats)) => void;
  setResearch: (research: ResearchState | ((prev: ResearchState) => ResearchState)) => void;
  setTickNumber: (n: number | ((prev: number) => number)) => void;
  setGameSpeed: (speed: number | ((prev: number) => number)) => void;
  setDeltas: (d: StatDeltas) => void;
  setSimBreakdown: (b: SimulationBreakdown) => void;
  setViolationTiles: (tiles: { x: number; y: number }[]) => void;
  setMilestones: (ms: Milestone[] | ((prev: Milestone[]) => Milestone[])) => void;
  refreshConnectivity: () => void;
  resetSandbox: () => void;
};

export type GameStore = GameStoreState & GameStoreActions;

export const useGameStore = create<GameStore>()(
  subscribeWithSelector((set, get) => ({
    showLanding: true,
    currentPage: 'landing',
    gameStarted: false,
    gameStatus: 'playing',
    aiEnabled: true,
    grid: simulationEngine.createInitialGrid(),
    stats: initialStats(),
    research: initialResearch(),
    tickNumber: 0,
    gameSpeed: 1,
    deltas: { budget: 0, compliance: 0, threat: 0 },
    simBreakdown: EMPTY_BREAKDOWN,
    violationTiles: [],
    milestones: MILESTONES,
    policyEffectNewsIds: [],

    setShowLanding: (showLanding) => set({ showLanding }),
    setCurrentPage: (currentPage) => set({ currentPage }),
    setGameStarted: (gameStarted) => {
      set({ gameStarted });
      uiStore.setGameStarted(gameStarted);
    },
    setGameStatus: (gameStatus) => set({ gameStatus }),
    setAiEnabled: (aiEnabled) => set({ aiEnabled }),
    setGrid: (grid) =>
      set((s) => ({
        grid: typeof grid === 'function' ? grid(s.grid) : grid,
      })),
    setStats: (stats) =>
      set((s) => ({
        stats: typeof stats === 'function' ? stats(s.stats) : stats,
      })),
    setResearch: (research) =>
      set((s) => ({
        research: typeof research === 'function' ? research(s.research) : research,
      })),
    setTickNumber: (tickNumber) =>
      set((s) => ({
        tickNumber: typeof tickNumber === 'function' ? tickNumber(s.tickNumber) : tickNumber,
      })),
    setGameSpeed: (gameSpeed) =>
      set((s) => ({
        gameSpeed: typeof gameSpeed === 'function' ? gameSpeed(s.gameSpeed) : gameSpeed,
      })),
    setDeltas: (deltas) => set({ deltas }),
    setSimBreakdown: (simBreakdown) => set({ simBreakdown }),
    setViolationTiles: (violationTiles) => set({ violationTiles }),
    setMilestones: (milestones) =>
      set((s) => ({
        milestones: typeof milestones === 'function' ? milestones(s.milestones) : milestones,
      })),
    refreshConnectivity: () => {
      const connected = simulationEngine.calculateConnectivity(get().grid);
      const changed = connected.some((row, y) =>
        row.some((tile, x) => tile.isConnected !== get().grid[y][x].isConnected)
      );
      if (changed) set({ grid: connected });
    },
    resetSandbox: () =>
      set({
        gameStarted: false,
        gameStatus: 'playing',
        grid: simulationEngine.createInitialGrid(),
        stats: initialStats(),
        research: initialResearch(),
        tickNumber: 0,
        gameSpeed: 1,
        deltas: { budget: 0, compliance: 0, threat: 0 },
        simBreakdown: EMPTY_BREAKDOWN,
        violationTiles: [],
        milestones: MILESTONES,
        policyEffectNewsIds: [],
      }),
  }))
);

/** Imperative access for tick loop (avoids stale closures) */
export const gameStore = {
  getState: () => useGameStore.getState(),
  setState: useGameStore.setState,
  subscribe: useGameStore.subscribe,
};

/** Shallow selectors for Three.js — subscribe only to grid/stats slices */
export const useGridState = () => useGameStore((s) => s.grid);
export const useStatsState = () => useGameStore((s) => s.stats);
export const useGameSpeed = () => useGameStore((s) => s.gameSpeed);
export const useTickFeedback = () =>
  useGameStore(
    useShallow((s) => ({
      deltas: s.deltas,
      simBreakdown: s.simBreakdown,
      violationTiles: s.violationTiles,
    }))
  );